import React, { useEffect, useState } from 'react';
import { Route, Routes, useNavigate, Navigate } from 'react-router-dom';
import { FiMail, FiBook, FiHelpCircle } from 'react-icons/fi';
import { auth, db } from '../../firebase';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { doc, getDoc } from 'firebase/firestore';
import {
  selectActiveSubscription,
  selectIsLoading,
  selectUser,
  selectTilesDetails,
  selectRestrictedDataError,
  fetchData,
} from '../../../../mainApp/features/DataSlice/dataSlice';
import LoadingPage from '../../TechnicalTabs/LoadingPage';
import ErrorPage from '../../TechnicalTabs/ErrorPage';
import { onAuthStateChanged } from 'firebase/auth';
import { handleUnauthorizedAccess } from '../../Utility/handleUnauthorizedAccess';
import {
  SettingsWrapper,
  LeftMenu,
  MenuItem,
  ContentArea,
  ContentHeader,
  ContentSection,
  SectionTitle,
  SectionDescription,
  ManageButton,
  PlanInfo,
  PlanTitle,
  PlanSubtitle,
  HorizontalLine,
  SpanPremium,
  ContactLink,
  ComingSoonBadge
} from '../styled';

const ContactContent = () => (
  <>
    <ContentHeader>Kontakt</ContentHeader>
    <ContentSection>
      <SectionTitle>Skontaktuj się z nami</SectionTitle>
      <SectionDescription>
        Masz pytania lub potrzebujesz pomocy? Jesteśmy tutaj, aby Ci pomóc.<br/> Możesz skontaktować się z nami poprzez:
      </SectionDescription>
      <ContentSection>
        <SectionTitle>Email</SectionTitle>
        <SectionDescription>
          Napisz do nas na adres: <ContactLink href="mailto:kontakt@dragonscape.pl">kontakt@dragonscape.pl</ContactLink>
        </SectionDescription>
      </ContentSection>
      <ContentSection>
        <SectionTitle>Chat</SectionTitle>
        <SectionDescription>
          Skorzystaj z ikony chatu w prawej dolnej części ekranu. <br/>
          Obsługujemy zapytania codziennie od 10:00 do 20:00 (CET)
        </SectionDescription>
      </ContentSection>
      <ContentSection>
        <SectionTitle>Formularz</SectionTitle>
        <SectionDescription>
          Wypełnij formularz kontaktowy na naszej stronie internetowej. Kliknij <ContactLink href="https://dragonscape.pl/kontakt" target="_blank">tutaj</ContactLink>, aby przejść do formularza.
        </SectionDescription>
      </ContentSection>
    </ContentSection>
  </>
);

const MaterialsContent = () => (
  <>
 
    <ContentHeader>
      Materiały
       <ComingSoonBadge>WKRÓTCE</ComingSoonBadge>
    </ContentHeader>
    <ContentSection>
      <SectionTitle>Materiały pomocnicze</SectionTitle>
      <SectionDescription>
        Tu znajdziesz przydatne materiały i instrukcje dotyczące korzystania z aplikacji.
      </SectionDescription>
      <ContentSection>
        <SectionTitle>Ebooki</SectionTitle>
        <SectionDescription>
          Gotowe pigułki wiedzy, o tym jak efektywnie korzystać z AI i pisać optymalne prompty, w celu uzyskania najbardziej pożądanych rezultatów np. filmów czy obrazów bez niechcianych dodatków i błędów.
        </SectionDescription>
      </ContentSection>
      <ContentSection>
        <SectionTitle>Wideo tutoriale</SectionTitle>
        <SectionDescription>
          Filmy instruktażowe pomagające w zrozumieniu funkcji aplikacji.
        </SectionDescription>
      </ContentSection>
    </ContentSection>
  </>
);

const FAQContent = () => (
  <>
    <ContentHeader>FAQ</ContentHeader>
    <ContentSection>
      <SectionTitle>Często zadawane pytania</SectionTitle>
      <SectionDescription>
        Odpowiedzi na najczęściej zadawane pytania dotyczące aplikacji.
      </SectionDescription>
      <ContentSection>
        <SectionTitle>Jak mogę zmienić swój plan subskrypcji?</SectionTitle>
        <SectionDescription>
          Możesz zmienić plan w ustawieniach aplikacji w zakładce "Plany i płatności".
        </SectionDescription>
      </ContentSection>
      <ContentSection>
        <SectionTitle>Jak działają tokeny?</SectionTitle>
        <SectionDescription>
          Tokeny są używane do generowania treści. Każda operacja, która wymaga udziału AI, kosztuje określoną liczbę tokenów.
        </SectionDescription>
      </ContentSection>
      <ContentSection>
        <SectionTitle>Jak mogę odzyskać/zmienić hasło?</SectionTitle>
        <SectionDescription>
          Użyj opcji "Zapomniałem hasła" na ekranie logowania lub opcji "Zmiana hasła" w ustawieniach aplikacji.
        </SectionDescription>
      </ContentSection>
      <ContentSection>
        <SectionTitle>Jakie są limity użytkowania?</SectionTitle>
        <SectionDescription>
          Limity zależą od wybranego planu subskrypcji:
                  <ul style={{ margin: '10px 0 0 20px' }}>
            <li><b>Standard:</b> Dowolna ilość zapytań do asystentów Standard w ramach limitu 3500 tokenów/msc. Ograniczona ilość zapytań do asystentów Premium (5 do wyszukiwarki, 3 zapytania do pozostałych). Tokeny resetują się do poziomu 3500 co miesiąc.</li>
            <li><b>Premium:</b> Dowolna ilość zapytań do asystentów Standard oraz Premium w ramach limitu 7000 tokenów/msc. Tokeny resetują się do poziomu 7000 co miesiąc.</li>
          </ul> 
        </SectionDescription>
      </ContentSection>
    </ContentSection>
  </>
);



const Help = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [initialLoading, setInitialLoading] = useState(true);
  const isLoading = useSelector(selectIsLoading);
  const user = useSelector(selectUser);
  const tilesDetails = useSelector(selectTilesDetails);
  const restrictedDataError = useSelector(selectRestrictedDataError);

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  useEffect(() => {
    dispatch(fetchData());
  }, [dispatch]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          dispatch(fetchData());
          const ref = doc(db, 'Users', currentUser.uid);
          await getDoc(ref);
          await delay(600);
        } catch (error) {
          console.error('Błąd pobierania danych użytkownika:', error);
          await delay(600);
        }
      } else {
        navigate('/login');
      }
      setInitialLoading(false);
    });
    return () => unsubscribe();
  }, [dispatch, navigate]);

  useEffect(() => {
    if (restrictedDataError) {
      navigate('/unauthorized');
    }
  }, [restrictedDataError, navigate]);

  const menuItems = [
    {
      path: 'contact',
      name: 'Kontakt',
      component: <ContactContent />,
      icon: <FiMail />,
    },
    {
      path: 'materials',
      name: 'Materiały',
      component: <MaterialsContent />,
      icon: <FiBook />,
    },
    {
      path: 'faq',
      name: 'FAQ',
      component: <FAQContent />,
      icon: <FiHelpCircle />,
    },
  ];

  if (initialLoading || (isLoading === 'loading' && tilesDetails.length === 0)) {
    return <LoadingPage />;
  }

  if (restrictedDataError) {
    return <ErrorPage />;
  }

  if (!user) {
    return <LoadingPage isHighZIndex={true} />;
  }

  if (tilesDetails.length > 0) {
    return (
      <SettingsWrapper>
        <LeftMenu>
          {menuItems.map((item) => (
            <MenuItem key={item.path} to={item.path}>
              {item.icon}
              <span>{item.name}</span>
            </MenuItem>
          ))}
        </LeftMenu>
        <ContentArea>
          <Routes>
            <Route index element={<Navigate to={menuItems[0].path} replace />} />
            {menuItems.map((item) => (
              <Route key={item.path} path={item.path} element={item.component} />
            ))}
          </Routes>
        </ContentArea>
      </SettingsWrapper>
    );
  } else if (isLoading === 'success') {
    // Jeśli ładowanie zakończone, a danych wciąż nie ma
    return handleUnauthorizedAccess();
  }

  // Domyślnie pokazuj stronę ładowania, jeśli żadne inne warunki nie są spełnione
  return <LoadingPage />;
};

export default Help; 